import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_labresult-hub-1/artifacts/td8pjg41_FACTUAL%20LOGO.png";
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_9e3eb863-9f56-4b53-ae60-1c92c3c49a59/artifacts/1ra7tmot_48b30cae-ac6d-4efa-818e-9ee28724a521.JPG";

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'patient',
    date_of_birth: '',
    gender: '',
    preferred_currency: 'NGN'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const { confirmPassword, ...userData } = formData;
    const result = await register(userData);
    setLoading(false);

    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex" data-testid="register-page">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:flex lg:flex-1 items-center justify-center p-12 relative"
        style={{
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70"></div>
        <div className="relative max-w-lg text-white z-10">
          <div className="flex items-center gap-3 mb-6">
            <img 
              src={LOGO_URL}
              alt="Factual Medical Logo" 
              className="w-16 h-16 rounded-full object-cover bg-white"
            />
            <div>
              <h2 className="text-2xl font-bold">FACTUAL MEDICAL</h2>
              <p className="text-slate-300 text-sm">DIAGNOSTIC CENTRE</p>
            </div>
          </div>
          <p className="text-slate-200 text-lg">
            Create your account to access comprehensive medical diagnostics, 
            accurate results, and seamless healthcare management.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              'Book lab tests online',
              'Home sample collection',
              'Digital medical records',
              'Accurate & reliable results'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-100">
                <span className="w-6 h-6 bg-sky-600 rounded-full flex items-center justify-center text-xs">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-8 group" data-testid="register-logo-link">
            <img 
              src={LOGO_URL}
              alt="Factual Medical Logo" 
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight block">Factual Medical</span>
              <span className="text-sm text-slate-600 dark:text-slate-400">Diagnostic Centre</span>
            </div>
          </Link>

          <div className="card-clinical">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Create your account</h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">Start your health journey with us</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name" className="text-slate-700 dark:text-slate-300">Full Name *</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  placeholder="Enter your full name"
                  className="input-clinical mt-1"
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@example.com"
                  className="input-clinical mt-1"
                  data-testid="register-email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">Password *</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="••••••••"
                      className="input-clinical pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-700 dark:text-slate-300">Confirm *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    placeholder="••••••••"
                    className="input-clinical mt-1"
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-slate-700 dark:text-slate-300">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+234 XXX XXX XXXX"
                  className="input-clinical mt-1"
                  data-testid="register-phone-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gender" className="text-slate-700 dark:text-slate-300">Gender</Label>
                  <Select 
                    value={formData.gender || undefined} 
                    onValueChange={(v) => handleChange('gender', v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="register-gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="dob" className="text-slate-700 dark:text-slate-300">Date of Birth</Label>
                  <Input
                    id="dob"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                    className="input-clinical mt-1"
                    data-testid="register-dob-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-700 dark:text-slate-300">Account Type</Label>
                <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                  <SelectTrigger className="mt-1" data-testid="register-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="lab_tech">Lab Technician</SelectItem>
                    <SelectItem value="lab_scientist">Laboratory Scientist</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-start gap-2">
                <input type="checkbox" className="mt-1 rounded border-slate-300 dark:border-slate-600" required />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  I agree to the{' '}
                  <Link to="/terms" className="text-sky-600 hover:text-sky-700 dark:text-sky-400">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-sky-600 hover:text-sky-700 dark:text-sky-400">Privacy Policy</Link>
                </span>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-sky-600 hover:bg-sky-700"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
              Already have an account?{' '}
              <Link to="/login" className="text-sky-600 hover:text-sky-700 dark:text-sky-400 font-medium" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
